"use client";

/**
 * IntercomFacade - Lightweight chat button placeholder
 *
 * Shows a CSS-only chat button before Intercom loads.
 * When clicked, triggers Intercom to load and show.
 * Automatically hides once real Intercom launcher appears.
 *
 * CRITICAL: Dimensions and POSITIONING must EXACTLY match Intercom's launcher to prevent layout shift
 * Intercom launcher: 48x48px visible, positioned bottom:20px right:20px (desktop), bottom:70px right:20px (mobile)
 * See globals.css for mobile positioning override on real Intercom launcher
 */
import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { markFacadeOpenRequest, useIntercom } from "./IntercomProvider";

// support_opened: the provider records each open as it happens; the tracker that sends them
// (src/lib/analytics/support-opened.ts) is imported only once window.Intercom exists or the facade is pressed, so it
// stays out of every page's initial bundle. Opens before it arrives are sent when it does.
function startSupportTracking(): void {
  import("@/lib/analytics/support-opened").then(m => m.registerSupportOpenTracking()).catch(() => {});
}
interface IntercomFacadeProps {
  className?: string;
}

// Helper to check for Intercom launcher elements
function checkForIntercomLauncher(): boolean {
  return !!(document.querySelector(".intercom-lightweight-app-launcher") || document.querySelector(".intercom-launcher-frame") || document.querySelector(".intercom-launcher") || document.querySelector('iframe[name="intercom-launcher-frame"]'));
}
export function IntercomFacade(t0) {
  const $ = _c(22);
  const {
    className
  } = t0;
  const {
    show,
    isLoaded,
    isLoading
  } = useIntercom();
  const [isHidden, setIsHidden] = useState(false);
  let t1;
  let t2;
  if ($[0] !== isHidden || $[1] !== isLoading) {
    t1 = () => {
      if (!isLoading || isHidden) {
        return;
      }
      if (checkForIntercomLauncher()) {
        setIsHidden(true);
        return;
      }
      const interval = setInterval(() => {
        if (checkForIntercomLauncher()) {
          setIsHidden(true);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    };
    t2 = [isLoading, isHidden];
    $[0] = isHidden;
    $[1] = isLoading;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== isHidden || $[5] !== isLoaded) {
    t3 = () => {
      if (!isLoaded || isHidden) {
        return;
      }
      if (checkForIntercomLauncher()) {
        setIsHidden(true);
        return;
      }
      const interval_0 = setInterval(() => {
        if (checkForIntercomLauncher()) {
          setIsHidden(true);
          clearInterval(interval_0);
        }
      }, 100);
      const timeout = setTimeout(() => {
        clearInterval(interval_0);
        setIsHidden(true);
      }, 2000);
      return () => {
        clearInterval(interval_0);
        clearTimeout(timeout);
      };
    };
    t4 = [isLoaded, isHidden];
    $[4] = isHidden;
    $[5] = isLoaded;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== isLoaded || $[9] !== isLoading) {
    t5 = [isLoaded, isLoading];
    $[8] = isLoaded;
    $[9] = isLoading;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(_temp, t5);
  let t6;
  if ($[11] !== show) {
    t6 = () => {
      markFacadeOpenRequest();
      show();
      startSupportTracking();
    };
    $[11] = show;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleClick = t6;
  if (isHidden) {
    return null;
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <style>{"\n                @media (max-width: 640px) {\n                    html[data-demo] #intercom-facade-btn {\n                        bottom: 70px !important;\n                    }\n                }\n                @keyframes intercom-facade-spin { to { transform: rotate(360deg); } }\n            "}</style>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: 2147483000,
      width: "48px",
      height: "48px",
      padding: 0,
      margin: 0,
      borderRadius: "50%",
      backgroundColor: "#141414",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "rgba(0, 0, 0, 0.06) 0px 1px 6px 0px, rgba(0, 0, 0, 0.16) 0px 2px 32px 0px",
      transition: "transform 0.15s ease, box-shadow 0.15s ease"
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isLoading) {
    t9 = isLoading ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{
      animation: "intercom-facade-spin 1s linear infinite"
    }}><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" fill="none" /><path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" /></svg> : <svg width="24" height="24" viewBox="0 0 28 32" fill="white"><path d="M28,32 C28,32 23.2863266,30.1450667 19.4727818,28.6592 L3.43749107,28.6592 C1.53921989,28.6592 0,27.0272 0,25.0144 L0,3.6448 C0,1.632 1.53921989,0 3.43749107,0 L24.5615088,0 C26.45978,0 27.9989999,1.632 27.9989999,3.6448 L27.9989999,22.0490667 L28,22.0490667 L28,32 Z M23.8614088,20.0181333 C23.5309223,19.6105242 22.9540812,19.5633836 22.5692242,19.9125333 C22.5392199,19.9392 19.5537934,22.5941333 13.9989999,22.5941333 C8.51321617,22.5941333 5.48178311,19.9584 5.4277754,19.9104 C5.04295119,19.5629428 4.46760991,19.6105095 4.13759108,20.0170667 C3.97913051,20.2124916 3.9004494,20.4673395 3.91904357,20.7249415 C3.93763774,20.9825435 4.05196575,21.2215447 4.23660523,21.3888 C4.37862552,21.5168 7.77411059,24.5386667 13.9989999,24.5386667 C20.2248893,24.5386667 23.6203743,21.5168 23.7623946,21.3888 C23.9467342,21.2215726 24.0608642,20.9827905 24.0794539,20.7254507 C24.0980436,20.4681109 24.0195551,20.2135019 23.8614088,20.0181333 Z" /></svg>;
    $[15] = isLoading;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== className || $[18] !== handleClick || $[19] !== isLoading || $[20] !== t9) {
    t10 = <>{t7}<button id="intercom-facade-btn" onClick={handleClick} disabled={isLoading} aria-label="Open chat support" className={className} style={t8} onMouseEnter={_temp2} onMouseLeave={_temp3}>{t9}</button></>;
    $[17] = className;
    $[18] = handleClick;
    $[19] = isLoading;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp3(e_0) {
  e_0.currentTarget.style.transform = "scale(1)";
}
function _temp2(e) {
  e.currentTarget.style.transform = "scale(1.08)";
}
function _temp() {
  if (typeof window !== "undefined" && typeof window.Intercom === "function") {
    startSupportTracking();
  }
}